import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Auto-recovers from stale-chunk errors after a deploy.
 *
 * The SPA lazy-loads route chunks. When a new deploy ships, the user's
 * in-memory `index.html` still references the OLD chunk hashes; the
 * OLD chunks may have been removed from the server. When the user
 * navigates to a route that wasn't already loaded, `import()` rejects
 * with "Failed to fetch dynamically imported module" and React unmounts
 * the route. Without this boundary, the user sees a raw stack trace and
 * has to manually refresh.
 *
 * Strategy: catch the error, force a hard reload via
 * `window.location.reload()`. The reload re-fetches `index.html`, which
 * now references the current chunk hashes; the user lands back on the
 * page they were trying to visit with zero ceremony.
 *
 * Why this and not a service-worker-driven solution: we don't ship a
 * service worker. Even when we do, this boundary is still the right
 * last-resort handler — it's a single render-time catch with no extra
 * runtime cost when nothing is wrong.
 *
 * A guard caps to one reload attempt per session via `sessionStorage`
 * so a genuinely-broken-everywhere build can't reload-loop a user.
 */

const RELOAD_GUARD_KEY = "shotflux:chunk-error-reload-attempted";

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Importing a module script failed/i,
];

/** Exported for unit testing — detects the chunk-load-failure signatures. */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message || "";
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

interface ChunkErrorBoundaryProps {
  children: ReactNode;
  /** Renderable to show if a non-chunk error is caught — defaults to a tiny error pane. */
  fallback?: ReactNode;
  /** Override for the reload side-effect. Defaults to `window.location.reload()`.
   *  Tests inject a spy; production leaves it default. */
  onChunkError?: () => void;
}

interface ChunkErrorBoundaryState {
  caughtNonChunkError: boolean;
}

export class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  override state: ChunkErrorBoundaryState = { caughtNonChunkError: false };

  static getDerivedStateFromError(error: unknown): ChunkErrorBoundaryState {
    if (isChunkLoadError(error)) {
      // Caught a chunk error — schedule reload in componentDidCatch where
      // we have access to `window`. Don't render the fallback for chunk
      // errors; the reload will replace the document.
      return { caughtNonChunkError: false };
    }
    return { caughtNonChunkError: true };
  }

  override componentDidCatch(error: unknown, _info: ErrorInfo) {
    if (!isChunkLoadError(error)) return;
    if (typeof window === "undefined") return;

    // Reload-loop guard: only reload once per browser session. If the
    // user lands here twice in the same session, something is genuinely
    // broken — show the fallback instead.
    let alreadyTried = false;
    try {
      alreadyTried = window.sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
    } catch {
      // sessionStorage can throw in some private-browsing modes — fall
      // through to the reload anyway; better to risk a loop on a
      // unrecoverable error than not auto-heal in the common case.
    }

    if (alreadyTried) {
      this.setState({ caughtNonChunkError: true });
      return;
    }

    try {
      window.sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
    } catch {
      // ignore; reload below is the important bit
    }

    const reload = this.props.onChunkError ?? (() => window.location.reload());
    reload();
  }

  override render() {
    if (this.state.caughtNonChunkError) {
      return (
        this.props.fallback ?? (
          <div
            role="alert"
            style={{
              padding: "24px",
              fontFamily: "system-ui, -apple-system, sans-serif",
              color: "#111114",
              maxWidth: "560px",
              margin: "48px auto",
            }}
          >
            <h1 style={{ margin: "0 0 12px 0", font: "500 24px/1.2 Georgia, serif" }}>
              Something went wrong.
            </h1>
            <p style={{ margin: 0, lineHeight: 1.5 }}>
              Please reload the page. If the problem persists, contact support.
            </p>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
